import { Alert, Stack, Table, Text } from '@mantine/core';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { PageTitle, QueryState } from '../../ui/components';
import { formatDate } from '../../ui/format';
import { useWorkplace } from '../access';
import { unwrap, useDb } from '../supabase';
import { AdminOnly } from './AdminOnly';

/** A3 Office users (Admin): DPAs per company. Invite and revoke come with Task 5 (`invite-member`). */
export function AdminOfficeUsersPage() {
  const { t } = useTranslation('dashboard');
  const db = useDb();
  const { access } = useWorkplace();
  const members = useQuery({
    queryKey: ['admin', 'members'],
    enabled: access.isAdmin,
    queryFn: async () => {
      const rows = unwrap(
        await db
          .from('company_members')
          .select('id, company_id, user_id, role, created_at, revoked_at')
          .order('created_at'),
      );
      const people = unwrap(
        await db
          .from('profiles')
          .select('id, full_name, email')
          .in(
            'id',
            rows.map((r) => r.user_id),
          ),
      );
      return { rows, people: new Map(people.map((p) => [p.id, p])) };
    },
  });
  const companyName = (id: string) => access.companies.find((c) => c.id === id)?.name ?? '';
  return (
    <AdminOnly>
      <PageTitle title={t('admin.officeUsersTitle')} />
      <Stack>
        <Alert color="blue">{t('admin.inviteTask5')}</Alert>
        <QueryState loading={members.isPending} error={members.error}>
          {() => (
            <Table striped>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>{t('admin.person')}</Table.Th>
                  <Table.Th>{t('admin.company')}</Table.Th>
                  <Table.Th>{t('admin.role')}</Table.Th>
                  <Table.Th>{t('admin.access')}</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {(members.data?.rows ?? []).map((m) => {
                  const person = members.data?.people.get(m.user_id);
                  return (
                    <Table.Tr key={m.id}>
                      <Table.Td>
                        <Text fw={600}>{person?.full_name ?? ''}</Text>
                        <Text size="sm">{person?.email ?? ''}</Text>
                      </Table.Td>
                      <Table.Td>{companyName(m.company_id)}</Table.Td>
                      <Table.Td>{t('admin.dpa')}</Table.Td>
                      <Table.Td>
                        {m.revoked_at === null
                          ? t('admin.since', { date: formatDate(m.created_at) })
                          : t('admin.ended', { date: formatDate(m.revoked_at) })}
                      </Table.Td>
                    </Table.Tr>
                  );
                })}
              </Table.Tbody>
            </Table>
          )}
        </QueryState>
      </Stack>
    </AdminOnly>
  );
}
